import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import {
  ACCEPTED_VIDEO_EXTENSIONS,
  type AssistantAttachment,
  type FieldSession,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import TextArea from '@carbon/react/es/components/TextArea';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import AiGenerate from '@carbon/icons-react/es/AiGenerate';
import Add from '@carbon/icons-react/es/Add';
import Close from '@carbon/icons-react/es/Close';
import Send from '@carbon/icons-react/es/Send';

import { apiFetch } from '../../api/client.js';
import { useAuthStore } from '../../auth/store.js';
import { useT } from '../../i18n/index.js';
import { useMultipartUpload } from '../upload/use-multipart-upload.js';
import { ProposalCard } from './ProposalCard.js';
import { useAssistant } from './use-assistant.js';

/** Rangs de rôle : l'adoption d'un modèle exige EDITOR+ (comme l'endpoint /models/adopt). */
const ROLE_RANK: Record<string, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};
const EDITOR_RANK = 2;

/**
 * Panneau de l'assistant IA (ADR-0016). On peut y **joindre une vidéo** (ADR-0027, incrément 1) : le
 * fichier part par le **même upload multipart pré-signé** que la page Data — l'octet ne transite ni
 * par l'assistant ni par l'API (§2). Seule une **référence** (id + titre) accompagne ensuite le
 * message ; le serveur revalide l'appartenance à l'organisation.
 */
export function AssistantPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const { status, messages, sending, error, send } = useAssistant();
  const { progress, upload } = useMultipartUpload();
  const currentUser = useAuthStore((state) => state.currentUser);
  // L'action proposée (adoption) exige EDITOR+ : sinon la carte affiche l'exigence, sans bouton.
  const canAct = (ROLE_RANK[currentUser?.memberships[0]?.role ?? 'VIEWER'] ?? 0) >= EDITOR_RANK;
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState<AssistantAttachment | null>(null);
  const [fieldSessionId, setFieldSessionId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const available = status?.enabled === true;
  const uploading =
    progress.state === 'preparing' ||
    progress.state === 'uploading' ||
    progress.state === 'completing';
  // Joindre exige une session d'accueil : `POST /field-sessions/default` (EDITOR+). Un rôle lecteur
  // reçoit 404 et n'a alors pas le bouton — cohérent avec les droits d'upload de la page Data.
  const canAttach = available && fieldSessionId !== null;

  // Session par défaut, une fois, à l'ouverture. Échec (lecteur) ⇒ pas de pièce jointe, sans bruit.
  useEffect(() => {
    if (!open) return;
    let active = true;
    void apiFetch<FieldSession>('/field-sessions/default', { method: 'POST' })
      .then((s) => active && setFieldSessionId(s.id))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [open]);

  // À l'ouverture, la saisie prend le focus (si active) ; Échap referme.
  useEffect(() => {
    if (!open) return;
    if (available) composerRef.current?.focus();
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, available, onClose]);

  // Toujours montrer le dernier message : on colle le journal en bas à chaque ajout.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, sending]);

  if (!open) return null;

  const attachFile = async (file: File | undefined) => {
    if (!file || !fieldSessionId || uploading) return;
    const videoId = await upload(file, fieldSessionId);
    // `upload` renvoie null en cas d'échec (format refusé, réseau) : le bandeau d'erreur du hook
    // (progress.error) est affiché plus bas. On ne joint que si le stockage a bien reçu la vidéo.
    if (videoId) setAttachment({ videoId, title: file.name });
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (!canAttach) return;
    void attachFile(event.dataTransfer.files[0]);
  };

  const submit = () => {
    if (!available || sending || uploading) return;
    const text = draft.trim();
    if (!text) return;
    const sent = attachment;
    setDraft('');
    setAttachment(null);
    void send(text, sent);
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Entrée envoie ; Maj+Entrée insère un retour à la ligne (convention des messageries).
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <aside
      className="ddp-assistant"
      role="dialog"
      aria-modal="false"
      aria-label={t('assistant.title')}
      onDragOver={(e) => {
        if (canAttach) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <header className="ddp-assistant__header">
        <span className="ddp-assistant__title">
          <AiGenerate size={20} />
          {t('assistant.title')}
        </span>
        <button
          type="button"
          className="ddp-assistant__close"
          onClick={onClose}
          aria-label={t('assistant.close')}
        >
          <Close size={20} />
        </button>
      </header>

      <div className="ddp-assistant__log" ref={logRef}>
        {status === null ? (
          <p className="ddp-assistant__hint">{t('common.loading')}</p>
        ) : !available ? (
          <InlineNotification
            kind="info"
            title={t('assistant.soonTitle')}
            subtitle={t('assistant.soonBody')}
            lowContrast
            hideCloseButton
          />
        ) : messages.length === 0 ? (
          <p className="ddp-assistant__hint">{t('assistant.intro')}</p>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`ddp-assistant__msg ddp-assistant__msg--${message.role}`}
            >
              {message.attachmentTitle && (
                <span className="ddp-assistant__attachment ddp-assistant__attachment--sent">
                  {message.attachmentTitle}
                </span>
              )}
              {message.text}
              {message.proposals && message.proposals.length > 0 && (
                <div className="ddp-assistant__proposals">
                  {message.proposals.map((proposal) => (
                    <ProposalCard
                      key={proposal.libraryItemId}
                      proposal={proposal}
                      canAct={canAct}
                    />
                  ))}
                </div>
              )}
            </div>
          ))
        )}
        {sending && <p className="ddp-assistant__hint">{t('assistant.thinking')}</p>}
        {progress.state === 'error' && progress.error && (
          <InlineNotification
            kind="error"
            title={t('common.error')}
            subtitle={progress.error}
            lowContrast
            hideCloseButton
          />
        )}
        {error && (
          <InlineNotification
            kind="error"
            title={t('common.error')}
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}
      </div>

      {dragging && canAttach && (
        <div className="ddp-assistant__drop">{t('assistant.dropHint')}</div>
      )}

      {(attachment || uploading) && (
        <div className="ddp-assistant__attachbar">
          <span className="ddp-assistant__attachment">
            {uploading
              ? `${t('assistant.attaching')} ${Math.round(progress.progress * 100)} %`
              : attachment?.title}
          </span>
          {attachment && !uploading && (
            <button
              type="button"
              className="ddp-assistant__attach-remove"
              onClick={() => setAttachment(null)}
              aria-label={t('assistant.attachRemove')}
            >
              <Close size={16} />
            </button>
          )}
        </div>
      )}

      <div className="ddp-assistant__composer">
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED_VIDEO_EXTENSIONS.join(',')}
          hidden
          onChange={(e) => {
            void attachFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <Button
          kind="ghost"
          size="md"
          renderIcon={Add}
          iconDescription={t('assistant.attach')}
          hasIconOnly
          disabled={!canAttach || sending || uploading}
          onClick={() => fileRef.current?.click()}
        />
        <TextArea
          ref={composerRef}
          labelText={t('assistant.inputLabel')}
          hideLabel
          rows={2}
          placeholder={available ? t('assistant.placeholder') : t('assistant.disabledPlaceholder')}
          value={draft}
          disabled={!available || sending}
          onKeyDown={onComposerKeyDown}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button
          kind="primary"
          size="md"
          renderIcon={Send}
          iconDescription={t('assistant.send')}
          hasIconOnly
          disabled={!available || sending || uploading || draft.trim().length === 0}
          onClick={submit}
        />
      </div>
    </aside>
  );
}
